import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Card, CardContent, Typography, Divider, useTheme, FormGroup, FormControlLabel, Checkbox, Grid } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import StrategicInsights from './StrategicInsights';
import ParametersLegend from './ParametersLegend';

const SideBySideOverview = () => {
  const theme = useTheme();
  // const data = useSelector((state) => state.competitorsReducer.competitorsAnalysisResults);
  // console.log('data', data);
  // Get data from Redux
  const sbsData = useSelector((state) => state.competitorsReducer.competitorsAnalysisResults?.side_by_side_analysis);

  // const tst = useSelector((state) => state.competitorsReducer.competitorsAnalysisResults.analysis_results);
  // console.log('tst', tst);
  // Access the analysis object
  const analysis = sbsData?.analysis || {};

  // Get report metadata
  const reportMetadata = analysis.report_metadata || {
    companies_analyzed: []
  };

  // Get competitive index data - now it's an array of objects with score and company
  const competitiveIndexArray = analysis['6_competitive_index'] || [];

  // Convert competitive index array to object for easier lookup
  const competitiveIndex = competitiveIndexArray.reduce((acc, item) => {
    acc[item.company] = item.score;
    return acc;
  }, {});

  // Get heatmap scorecard data
  const heatmapData = analysis['7_multi_dimensional_comparison_matrices']?.A_heatmap_scorecard || [];

  // Get parameter weights
  const parameterWeights = (analysis['4_weight_assignment'] || []).reduce((acc, param) => {
    acc[param.id] = param.weight_percent;
    return acc;
  }, {});

  // Get parameters (excluding company and total)
  const parameters =
    heatmapData.length > 0
      ? Object.keys(heatmapData[0] || {}).filter((key) => key !== 'company' && key !== 'total')
      : Object.keys(parameterWeights);

  // State to track which parameters are selected
  const [selectedParameters, setSelectedParameters] = useState(parameters.reduce((acc, param) => ({ ...acc, [param]: true }), {}));

  // Get only the parameters that are selected
  const filteredParameters = parameters.filter((param) => selectedParameters[param]);

  // Find top scoring company
  const topCompany = competitiveIndexArray.reduce(
    (top, current) => {
      return current.score > top.score ? current : top;
    },
    { company: '', score: 0 }
  );

  // Transform data for stacked bar chart with weighted values
  const stackedData =
    heatmapData?.map((item) => {
      // Start with the company name
      const result = { company: item.company };

      // Calculate weighted values for each parameter
      // (score / 5) * weight percentage = weighted score
      // If all scores were 5/5, the maximum weighted score would be 100
      parameters.forEach((param) => {
        if (selectedParameters[param]) {
          const paramWeight = parameterWeights[param];
          const weightedValue = (item[param] / 5) * paramWeight;
          result[param] = parseFloat(weightedValue.toFixed(1)); // Round to 1 decimal place
        }
      });

      return result;
    }) || [];

  // Generate colors for each parameter
  const getParameterColor = (index) => {
    const colors = [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.success.main,
      theme.palette.warning.main,
      theme.palette.error.main,
      theme.palette.info.main,
      '#8884d8',
      '#82ca9d',
      '#ffc658',
      '#ff8042'
    ];
    return colors[index % colors.length];
  };

  // Format parameter names for display
  const formatParameterName = (param) => {
    return param
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Handle checkbox change for parameter selection
  const handleParameterToggle = (param) => {
    setSelectedParameters({
      ...selectedParameters,
      [param]: !selectedParameters[param]
    });
  };

  // console.log(reportMetadata);
  // Custom tooltip for the stacked bar chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      // Find the company data from heatmapData for raw scores
      const companyData = heatmapData?.find((item) => item.company === label);
      const companyTotalScore = competitiveIndex[label];

      return (
        <Box sx={{ bgcolor: 'background.paper', p: 2, border: '1px solid #ccc', borderRadius: 1, maxWidth: 300 }}>
          <Typography variant="h6">{label}</Typography>
          <Divider sx={{ my: 1 }} />
          {payload.map((entry, index) => {
            const paramName = entry.dataKey;
            const rawScore = companyData?.[paramName] || 0;

            return (
              <Box key={`item-${index}`} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    bgcolor: entry.color,
                    mr: 1,
                    borderRadius: '50%'
                  }}
                />
                <Typography variant="body2" sx={{ mr: 'auto' }}>
                  {entry.name}:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {rawScore}/5
                </Typography>
              </Box>
            );
          })}
          <Divider sx={{ my: 1 }} />
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            Score: {companyTotalScore}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  // Handle empty or loading state
  if (!sbsData || Object.keys(sbsData).length === 0) {
    return (
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>
          Overview
        </Typography>
        <Card sx={{ boxShadow: 1 }}>
          <CardContent>
            <Typography variant="body1">Loading data or no data available for overview.</Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>
        Overview
      </Typography>

      {/* Stats Cards Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Companies Analyzed Card */}
        <Grid item xs={12} md={6} lg={3}>
          <Card
            sx={{
              height: '100%',
              boxShadow: 1,
              borderLeft: '4px solid',
              borderColor: 'primary.main',
              position: 'relative'
            }}
          >
            <CardContent>
              <Typography variant="subtitle1" color="text.secondary">
                Companies Analyzed
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1, mb: 1 }}>
                {reportMetadata.companies_analyzed.length}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {reportMetadata.companies_analyzed.join(', ')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Parameters Evaluated Card */}
        <Grid item xs={12} md={6} lg={3}>
          <Card
            sx={{
              height: '100%',
              boxShadow: 1,
              borderLeft: '4px solid',
              borderColor: 'info.main'
            }}
          >
            <CardContent>
              <Typography variant="subtitle1" color="text.secondary">
                Parameters Evaluated
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1, mb: 1 }}>
                {parameters.length}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Pricing, Reviews, Speed, Features, etc.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Score Card */}
        <Grid item xs={12} md={6} lg={3}>
          <Card
            sx={{
              height: '100%',
              boxShadow: 1,
              borderLeft: '4px solid',
              borderColor: 'success.main'
            }}
          >
            <CardContent>
              <Typography variant="subtitle1" color="text.secondary">
                Top Score
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1, mb: 1 }}>
                {topCompany.score}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {topCompany.company} leads the market
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Strategic Opportunities Card */}
        <Grid item xs={12} md={6} lg={3}>
          <Card
            sx={{
              height: '100%',
              boxShadow: 1,
              borderLeft: '4px solid',
              borderColor: 'warning.main'
            }}
          >
            <CardContent>
              <Typography variant="subtitle1" color="text.secondary">
                Strategic Opportunities
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1, mb: 1 }}>
                4
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Identified market white spaces
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Strategic Insights Section */}
      <Box sx={{ mb: 4 }}>
        <StrategicInsights />
      </Box>

      {/* Lower Grid with Chart and Insights */}
      <Grid container spacing={3}>
        {/* Competitive Landscape Chart - Full Width */}
        <Grid item xs={12}>
          <Card sx={{ height: '100%', boxShadow: 1 }}>
            <CardContent>
              <Typography variant="h5" sx={{ mb: 2 }}>
                Competitive Landscape
              </Typography>

              {/* Parameters Legend */}
              <ParametersLegend scoringParameters={analysis['3_scoring_parameters'] || []} />

              {/* Parameter Selection Checkboxes */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', mb: 3 }}>
                <FormGroup row>
                  {parameters.map((param, index) => (
                    <FormControlLabel
                      key={param}
                      control={
                        <Checkbox
                          checked={selectedParameters[param]}
                          onChange={() => handleParameterToggle(param)}
                          name={param}
                          sx={{
                            color: getParameterColor(index),
                            '&.Mui-checked': {
                              color: getParameterColor(index)
                            }
                          }}
                        />
                      }
                      label={formatParameterName(param)}
                    />
                  ))}
                </FormGroup>
              </Box>

              <Box sx={{ width: '100%', height: 400 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={stackedData}
                    margin={{
                      top: 20,
                      right: 30,
                      left: 20,
                      bottom: 5
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="company" />
                    <YAxis domain={[0, 100]} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    {filteredParameters.map((param) => (
                      <Bar
                        key={param}
                        dataKey={param}
                        stackId="a"
                        name={formatParameterName(param)}
                        fill={getParameterColor(parameters.indexOf(param))}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </Box>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
                Each bar shows weighted parameter scores. Perfect scores in all parameters would total 100.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Key Insights Card - Full Width below chart
        <Grid item xs={12}>
          <Card sx={{ 
            height: '100%', 
            // boxShadow: 1,
            borderRadius: 2,
            position: 'relative',
            isolation: 'isolate',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              padding: '1px',
              borderRadius: 'inherit',
              background: 'linear-gradient(135deg, rgba(103,58,183,0.5) 0%, rgba(103,58,183,0.5) 60%, rgba(103,58,183,0.2) 100%)',
              WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
              WebkitMaskComposite: 'xor',
              maskComposite: 'exclude',
              zIndex: -1
            },
            boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 4px 8px rgba(0,0,0,0.03)'
            },
            bgcolor: 'background.paper'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography 
                variant="h4" 
                sx={{ 
                  mb: 1, 
                  fontWeight: 700,
                  color: 'secondary.dark'
                }}
              >
                Key Insights
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Top strategic findings from the analysis
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6} lg={3}>
                  <Box 
                    sx={{ 
                      p: 2,
                      borderRadius: 2,
                      borderLeft: '3px solid',
                      borderColor: 'primary.main',
                      bgcolor: 'rgba(63, 81, 181, 0.05)',
                      height: '100%',
                      transition: 'transform 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)'
                      }
                    }}
                  >
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        mb: 1,
                        fontWeight: 600,
                        color: 'primary.main'
                      }}
                    >
                      Review Sentiment
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Most competitors have strong review sentiment, but some have low review counts.
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6} lg={3}>
                  <Box 
                    sx={{ 
                      p: 2,
                      borderRadius: 2,
                      borderLeft: '3px solid',
                      borderColor: 'info.main',
                      bgcolor: 'rgba(3, 169, 244, 0.05)',
                      height: '100%',
                      transition: 'transform 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)'
                      }
                    }}
                  >
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        mb: 1,
                        fontWeight: 600,
                        color: 'info.main'
                      }}
                    >
                      Time-to-Insight
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Speed is now table stakes; any lag is a competitive disadvantage.
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6} lg={3}>
                  <Box 
                    sx={{ 
                      p: 2,
                      borderRadius: 2,
                      borderLeft: '3px solid',
                      borderColor: 'warning.main',
                      bgcolor: 'rgba(255, 152, 0, 0.05)',
                      height: '100%',
                      transition: 'transform 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)'
                      }
                    }}
                  >
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        mb: 1,
                        fontWeight: 600,
                        color: 'warning.main'
                      }}
                    >
                      Pricing Transparency
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Many competitors lack transparent pricing, creating an opportunity for clear, flexible pricing models.
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6} lg={3}>
                  <Box 
                    sx={{ 
                      p: 2,
                      borderRadius: 2,
                      borderLeft: '3px solid',
                      borderColor: 'success.main',
                      bgcolor: 'rgba(76, 175, 80, 0.05)',
                      height: '100%',
                      transition: 'transform 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)'
                      }
                    }}
                  >
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        mb: 1,
                        fontWeight: 600,
                        color: 'success.main'
                      }}
                    >
                      Feature Differentiation
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      While core AI capabilities are common, integrations and specialized features create meaningful differentiation.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid> */}
      </Grid>
    </Box>
  );
};

export default SideBySideOverview;
